import React, { useState, useEffect } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useApi } from '../../hooks/useApi';
import Loader from '../common/Loader';
import './Users.css';

const formatDate = (val) => {
  if (val == null) return '—';
  const d = new Date(val);
  return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
};

const formatValue = (value) => {
  if (value == null) return '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const PAGE_SIZES = [10, 25, 50, 100];

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total]);
  for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) {
    pages.add(i);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(`gap-${i}`);
    result.push(sorted[i]);
  }
  return result;
};

const Users = () => {
  const { apiCall } = useApi();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [profileEdit, setProfileEdit] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [sortField, setSortField] = useState('last_login_at');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // 204-safe wrapper
  const apiCallNoBody = async (endpoint, options) => {
    try { return await apiCall(endpoint, options); }
    catch (err) { if (err instanceof SyntaxError) return null; throw err; }
  };

  const fetchUsers = async (page = currentPage, size = pageSize, q = debouncedSearch) => {
    setLoading(true);
    setError('');
    try {
      let url = `/admin/users?page=${page}&page_size=${size}`;
      if (q && q.trim()) url += `&q=${encodeURIComponent(q.trim())}`;
      const data = await apiCall(url);
      setUsers(data.users || []);
      // Support both envelope shapes: nested {pagination: {...}} or flat
      const pg = data.pagination || data;
      setTotal(pg.total || 0);
      setTotalPages(pg.total_pages || 1);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchUsers(currentPage, pageSize, debouncedSearch); }, [currentPage, pageSize, debouncedSearch]);

  const handleDeleteUser = async (firebaseUid) => {
    setDeleteLoading(true);
    setError('');
    try {
      await apiCallNoBody(`/admin/users/${firebaseUid}`, { method: 'DELETE' });
      setDeleteConfirm(null);
      await fetchUsers(currentPage, pageSize, debouncedSearch);
    } catch (err) {
      setError(err.message);
    }
    setDeleteLoading(false);
  };

  const handleUpdateProfile = async (firebaseUid, newProfile) => {
    setProfileSaving(true);
    setError('');
    try {
      await apiCall('/admin/users/set-profile', {
        method: 'POST',
        body: JSON.stringify({ user_id: firebaseUid, profile: newProfile }),
      });
      setProfileEdit(null);
      await fetchUsers(currentPage, pageSize, debouncedSearch);
    } catch (err) {
      setError(err.message);
    }
    setProfileSaving(false);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIndicator = ({ field }) => {
    if (sortField !== field) return <span className="sort-indicator inactive">↕</span>;
    return <span className="sort-indicator">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  // Client-side sort on current page
  const displayed = [...users].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading && users.length === 0) return <Loader />;

  return (
    <div className="users-page">
      <div className="page-header">
        <h2 className="page-title">Users ({total})</h2>
        <button onClick={() => fetchUsers(currentPage, pageSize, debouncedSearch)} className="btn-icon" disabled={loading}>
          <RefreshIcon className={loading ? 'spinning' : ''} sx={{ fontSize: '1.25rem' }} />
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="search-bar">
        <div className="search-input-wrapper">
          <SearchIcon className="search-icon" sx={{ fontSize: '1.25rem' }} />
          <input
            type="text"
            placeholder="Search by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="clear-search-btn">
              <CloseIcon sx={{ fontSize: '1rem' }} />
            </button>
          )}
        </div>
      </div>

      <div className="table-card">
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th className="th-sortable" onClick={() => handleSort('firebase_uid')}>
                  User ID <SortIndicator field="firebase_uid" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('email')}>
                  Email <SortIndicator field="email" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('profile')}>
                  Profile <SortIndicator field="profile" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('credits_remaining_seconds')}>
                  Credits (s) <SortIndicator field="credits_remaining_seconds" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('last_login_at')}>
                  Last Login <SortIndicator field="last_login_at" />
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 ? (
                <tr>
                  <td colSpan={6} className="no-results">
                    {debouncedSearch.trim() ? `No results for "${debouncedSearch.trim()}"` : 'No users found'}
                  </td>
                </tr>
              ) : (
                displayed.map((user) => (
                  <tr key={user.firebase_uid || user.id} className="user-row">
                    <td className="td-id" title={user.firebase_uid}>{user.firebase_uid || '—'}</td>
                    <td>{user.email || '—'}</td>
                    <td>
                      {user.profile
                        ? <span className={`badge ${user.profile === 'qarl_admin' ? 'badge-orange' : 'badge-gray'}`}>{user.profile}</span>
                        : '—'}
                    </td>
                    <td className="td-credits">
                      {user.credits_remaining_seconds != null ? user.credits_remaining_seconds : '—'}
                    </td>
                    <td className="td-date">{formatDate(user.last_login_at)}</td>
                    <td className="td-actions">
                      <button
                        className="btn-icon-sm"
                        title="View details"
                        onClick={() => setSelectedUser(user)}
                      >
                        <VisibilityIcon sx={{ fontSize: '1.125rem' }} />
                      </button>
                      <button
                        className="btn-icon-sm btn-shield"
                        title="Edit profile"
                        onClick={() => setProfileEdit({ firebase_uid: user.firebase_uid, profile: user.profile || 'user' })}
                      >
                        <AdminPanelSettingsIcon sx={{ fontSize: '1.125rem' }} />
                      </button>
                      <button
                        className="btn-icon-sm btn-delete"
                        title="Deactivate user"
                        onClick={() => setDeleteConfirm(user.firebase_uid)}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: '1.125rem' }} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="table-footer">
          <div className="rows-per-page">
            <span>Rows per page:</span>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
              {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <span className="pagination-info">
            {total === 0
              ? 'No results'
              : `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, total)} of ${total}`}
          </span>
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="page-btn page-nav"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                title="Previous page"
              >
                <ChevronLeftIcon sx={{ fontSize: '1.25rem' }} />
              </button>
              {getPageNumbers(currentPage, totalPages).map((p) =>
                typeof p === 'string' ? (
                  <span key={p} className="page-ellipsis">…</span>
                ) : (
                  <button
                    key={p}
                    className={`page-btn${p === currentPage ? ' active' : ''}`}
                    onClick={() => setCurrentPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                className="page-btn page-nav"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                title="Next page"
              >
                <ChevronRightIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">User Details</h3>
              <button onClick={() => setSelectedUser(null)} className="btn-close">
                <CloseIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
            <div className="modal-body">
              {Object.entries(selectedUser).map(([key, value]) => (
                <div className="detail-row" key={key}>
                  <span className="detail-label">{key}:</span>
                  <span className="detail-value detail-value-sm">{formatValue(value)}</span>
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-danger"
                onClick={() => { setSelectedUser(null); setDeleteConfirm(selectedUser.firebase_uid); }}
              >
                <DeleteOutlineIcon sx={{ fontSize: '1rem' }} /> Deactivate User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {profileEdit && (
        <div className="modal-overlay" onClick={() => !profileSaving && setProfileEdit(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit Profile</h3>
              <button onClick={() => setProfileEdit(null)} className="btn-close" disabled={profileSaving}>
                <CloseIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <span className="detail-label">User ID:</span>
                <span className="detail-value detail-value-sm">{profileEdit.firebase_uid}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Profile:</span>
                <select
                  className="detail-select"
                  value={profileEdit.profile}
                  onChange={(e) => setProfileEdit({ ...profileEdit, profile: e.target.value })}
                >
                  <option value="user">user</option>
                  <option value="qarl_admin">qarl_admin</option>
                </select>
              </div>
            </div>
            <div className="modal-footer modal-footer-right">
              <button
                className="btn btn-secondary"
                onClick={() => setProfileEdit(null)}
                disabled={profileSaving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleUpdateProfile(profileEdit.firebase_uid, profileEdit.profile)}
                disabled={profileSaving}
              >
                {profileSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => !deleteLoading && setDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Deactivate User</h3>
            </div>
            <div className="modal-body">
              <p className="confirm-text">
                Deactivate user <strong className="user-id-highlight">{deleteConfirm}</strong>?
              </p>
              <p className="confirm-subtext">
                This sets <code>is_active</code> to <code>false</code>. The user record stays in the database and can be reactivated later. API keys remain active.
              </p>
            </div>
            <div className="modal-footer modal-footer-right">
              <button
                className="btn btn-secondary"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleDeleteUser(deleteConfirm)}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deactivating...' : 'Deactivate User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
